/**
 * briefingA2.js — Briefing Circulation adapté moto A2 (moto puissante, permis A2).
 * Spécificités : positionnement 1/3 droit (hors agglo) / 1/3 gauche (agglo — portières),
 * trajectoire de sécurité adaptative à gauche ou à droite selon virage avec/sans visibilité.
 */

export const briefing = {
  title: 'Mon Briefing Moto A2',
  subtitle: 'Les 7 réflexes du pilote A2 avant et pendant la sortie',
  mantra: 'VOIR · ANTICIPER · SE POSITIONNER · ADAPTER · AGIR',
  slogan: 'Ma sécurité, ma responsabilité.',
  // Pas de carte visuelle pour l'A2 — contenu 100% textuel

  branches: [
    /* ─────────── 1. POSITIONNEMENT (spécifique A2) ─────────── */
    {
      num: 1,
      color: 'blue',
      title: 'Positionnement',
      subtitle: 'Sur la chaussée · A2',
      icon: 'MoveHorizontal',
      subCards: [
        {
          title: 'Hors agglomération',
          tone: 'ok',
          bullets: [
            '1/3 DROIT de ma voie',
            'Je vois loin dans les virages à droite',
            'Je garde une marge côté bas-côté',
            'Je reste visible pour ceux qui arrivent en face',
          ],
        },
        {
          title: 'En agglomération',
          tone: 'warn',
          bullets: [
            '1/3 GAUCHE de ma voie',
            "J'évite les ouvertures de portières",
            'Je gagne en visibilité dans le trafic',
            'Je m\'écarte des piétons qui traversent',
          ],
        },
      ],
      consigne: "Mon positionnement n'est jamais fixe : il s'adapte à l'environnement (agglo / hors-agglo) et aux dangers du moment.",
    },

    /* ─────────── 2. REGARD / ANTICIPATION ─────────── */
    {
      num: 2,
      color: 'emerald',
      title: 'Regard / Anticipation',
      subtitle: 'Voir plus loin qu\'en AM',
      icon: 'Eye',
      lookZones: [
        'Loin devant (plusieurs centaines de mètres)',
        'Toutes les intersections en approche',
        'Les côtés (véhicules, animaux, gibier)',
        'Les rétroviseurs toutes les 5-7 secondes',
        'Les zones masquées et angles morts',
      ],
      method: ['VOIR', 'ANALYSER', 'DÉCIDER', 'AGIR'],
      consigne: "Sur une A2, la vitesse augmente vite : mon regard doit toujours être plus loin que ma vitesse.",
    },

    /* ─────────── 3. VIRAGES A2 (avec ou sans visibilité) ─────────── */
    {
      num: 3,
      color: 'orange',
      title: 'Virages',
      subtitle: 'Le point critique en moto',
      icon: 'CornerDownRight',
      subCards: [
        {
          title: 'Virage avec visibilité',
          tone: 'ok',
          bullets: [
            'Je ralentis AVANT le virage',
            'Je regarde la SORTIE, pas devant ma roue',
            'Je construis une trajectoire large → serrée → large',
            'Accélération progressive dès la mi-virage',
          ],
        },
        {
          title: 'Virage sans visibilité',
          tone: 'warn',
          bullets: [
            'Je ralentis PLUS que nécessaire',
            "Je n'entre jamais trop vite",
            'Je ne coupe JAMAIS le virage',
            'Je reste capable de m\'arrêter sur la distance vue',
            'Je recherche la meilleure visibilité possible',
          ],
        },
      ],
      consigneForte: 'JE NE COUPE JAMAIS UN VIRAGE SANS VISIBILITÉ.',
    },

    /* ─────────── 4. TRAJECTOIRE DE SÉCURITÉ (adaptative droite/gauche) ─────────── */
    {
      num: 4,
      color: 'purple',
      title: 'Trajectoire de sécurité',
      subtitle: 'À droite ou à gauche selon le virage',
      icon: 'Route',
      keyPhrase: "La trajectoire de sécurité n'est PAS une ligne — c'est une intention adaptable.",
      subCards: [
        {
          title: 'Virage à droite',
          tone: 'ok',
          bullets: [
            'Je me place à GAUCHE de ma voie en entrée',
            'Je gagne en visibilité sur la sortie',
            'Je serre à la corde uniquement si visible',
            "J'évite la trajectoire des véhicules d'en face",
          ],
        },
        {
          title: 'Virage à gauche',
          tone: 'ok',
          bullets: [
            'Je me place à DROITE de ma voie en entrée',
            'Je vois plus loin dans le virage',
            'Je reste éloigné de la ligne médiane',
            "Je garde une marge face aux véhicules qui coupent",
          ],
        },
      ],
      consigne: "Je choisis toujours la trajectoire qui me laisse le plus de solutions — même si elle n'est pas la plus rapide.",
    },

    /* ─────────── 5. GIRATOIRE ─────────── */
    {
      num: 5,
      color: 'sky',
      title: 'Giratoire',
      subtitle: '3 temps, 1 discipline',
      icon: 'Redo2',
      phases: [
        { title: "Avant d'entrer", steps: ['Ralentir franchement', 'Observer', 'Choisir ma sortie', 'Vérifier les priorités'] },
        { title: 'Dans le giratoire', steps: ['Vitesse adaptée', 'Trajectoire stable', 'Regard vers la sortie', 'Rétroviseur avant sortie'] },
        { title: 'À la sortie', steps: ['Signaler', 'Contrôler', 'Se positionner sur ma voie', 'Reprendre progressivement'] },
      ],
      consigne: "Je ne suis jamais aveuglément la moto devant moi — je conduis MA moto.",
    },

    /* ─────────── 6. CIRCULATION EN GROUPE (A2 spécifique) ─────────── */
    {
      num: 6,
      color: 'lime',
      title: 'Circulation en groupe',
      subtitle: 'Leader · Suiveur · en quinconce',
      icon: 'Users',
      roles: [
        {
          role: 'Leader',
          icon: 'Crown',
          motto: 'Anticipe · informe · régule',
          bullets: [
            'Allure adaptée au moins expérimenté',
            'Signale les changements largement en avance',
            'Ne cherche pas à distancer le groupe',
            "Utilise les gestes ou l'intercom si équipé",
          ],
        },
        {
          role: 'Suiveur',
          icon: 'Bike',
          motto: 'Observe · marge · décision',
          bullets: [
            'Position en quinconce (jamais dans l\'axe direct)',
            'Distance de sécurité minimum : 2 secondes',
            "N'imite pas aveuglément la moto précédente",
            'Adapte son allure à SON niveau',
          ],
        },
      ],
      regle: 'Je ne prends JAMAIS de risque pour rattraper le groupe.',
    },

    /* ─────────── 7. COMMUNICATION / PERTE DU GROUPE ─────────── */
    {
      num: 7,
      color: 'red',
      title: 'Communication / Perte du groupe',
      subtitle: 'Signaler · rester serein',
      icon: 'Radio',
      communication: {
        motto: 'Simple · claire · anticipée',
        signals: ['Changement de direction (clignotant tôt)', 'Ralentissement important (feu stop pompé)', 'Arrêt (bras tendu vers le bas)', 'Danger identifié (main levée ou pied écarté)'],
      },
      perte: {
        title: 'SI JE PERDS LE GROUPE',
        headline: 'Je ne cherche pas à rattraper à tout prix.',
        steps: ['Garde mon calme', 'Respecte le Code', 'Allure normale', 'Point de regroupement prévu'],
      },
    },
  ],

  reflexes: [
    { num: 1, title: 'Je regarde', desc: 'Plus loin que ma vitesse.', icon: 'Eye', color: 'blue' },
    { num: 2, title: 'Je me positionne', desc: '1/3 droit hors agglo · 1/3 gauche en agglo.', icon: 'MapPin', color: 'emerald' },
    { num: 3, title: "J'adapte", desc: 'Ma vitesse à la visibilité, jamais à la limite.', icon: 'Gauge', color: 'orange' },
    { num: 4, title: 'Je garde une marge', desc: "Toujours une solution en réserve.", icon: 'Shield', color: 'red' },
    { num: 5, title: 'Je reste autonome', desc: "Je conduis MA moto, pas celle qui est devant.", icon: 'Brain', color: 'purple' },
  ],
};

export const briefingQuiz = {
  id: 'briefing-a2',
  title: 'QCM Briefing A2 · 5 réflexes clés',
  questions: [
    {
      q: "Hors agglomération, sur quelle partie de ma voie dois-je me positionner en moto A2 ?",
      choices: ["1/3 gauche", "Au milieu exact", "1/3 droit", "Sur la ligne blanche"],
      answer: 2,
      explain: "Hors agglo : 1/3 DROIT. Je vois loin dans les virages, je garde une marge côté bas-côté, je reste visible pour ceux qui arrivent en face.",
    },
    {
      q: "En agglomération, où me placer et pourquoi ?",
      choices: [
        "Au milieu, c'est plus safe",
        "1/3 GAUCHE, pour éviter les ouvertures de portières",
        "Sur le bord droit, tout contre les trottoirs",
        "Dans le sillage direct des voitures",
      ],
      answer: 1,
      explain: "En agglo : 1/3 GAUCHE. J'évite les portières qui s'ouvrent, je gagne en visibilité dans le trafic et je m'écarte des piétons qui traversent.",
    },
    {
      q: "Dans un virage à droite, quelle trajectoire de sécurité ?",
      choices: [
        "Serrer à la corde intérieure dès l'entrée",
        "Rester au milieu de la voie",
        "Me placer à GAUCHE de ma voie en entrée pour voir la sortie",
        "Rouler sur la ligne médiane",
      ],
      answer: 2,
      explain: "Virage à DROITE → j'entre en position GAUCHE de ma voie pour voir la sortie. Je serre uniquement si la visibilité le permet.",
    },
    {
      q: "Dans un virage à gauche sans visibilité, quelle trajectoire ?",
      choices: [
        "Serrer à gauche pour couper le virage",
        "Rester à DROITE de ma voie et ralentir plus que nécessaire",
        "Foncer pour être fixé",
        "Rouler dans l'axe médian",
      ],
      answer: 1,
      explain: "Virage à GAUCHE sans visibilité → je reste à DROITE de ma voie, je ralentis plus que nécessaire, je ne coupe JAMAIS le virage.",
    },
    {
      q: "En groupe moto, la position idéale du suiveur est :",
      choices: [
        "Directement derrière la moto précédente",
        "Collé au pare-chocs pour ne pas la perdre",
        "En quinconce, avec au moins 2 secondes de distance",
        "En zigzag pour amuser le groupe",
      ],
      answer: 2,
      explain: "Position en QUINCONCE (jamais dans l'axe direct) + distance minimum 2 secondes. Je n'imite pas aveuglément la moto précédente : je conduis MA moto.",
    },
  ],
};

export default briefing;
